const BASE_URL = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

/**
 * 多輪需求收集對話
 * @param {object} payload
 * @param {string}  payload.userMessage   - 本輪用戶輸入
 * @param {Array}   payload.chatHistory   - [{role,content}]
 * @param {boolean} payload.planFirst     - 是否要求先出 Plan
 * @param {string|null} payload.imageBase64 - 可選圖片（base64）
 * @param {string|null} payload.imageType   - e.g. 'image/png'
 * @returns {Promise<{reply,suggestions,readyToBuild,plan}>}
 */
export async function buildAppChat(payload) {
  const res = await fetch(`${BASE_URL}/api/build-app/chat`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 最終生成：後端根據 plan 呼叫各 ai-generate，建立 WorkflowDefinition / EFormDefinition 記錄
 * @param {object} plan  - plan_review 階段確認的 plan JSON
 * @returns {Promise<{workflowId,eformId,metaflowId,planType}>}
 */
export async function buildAppGenerate(plan) {
  const res = await fetch(`${BASE_URL}/api/build-app/generate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(plan)
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 建置嚮導完成：把各步驟選用的資源寫入 workflow 節點
 * @param {object} payload
 * @param {number|null} payload.workflowId
 * @param {string|null} payload.eformId
 * @param {string|null} payload.metaflowId
 * @param {string|null} payload.templateId
 * @param {string|null} payload.datasetId
 * @param {string|null} payload.aiStaffId
 * @param {string[]}    payload.kbDocumentIds
 */
export async function buildAppFinalize(payload) {
  const res = await fetch(`${BASE_URL}/api/build-app/finalize`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 保存或更新對話歷史 session
 * @param {object} payload
 * @param {string|null} payload.buildAppId   - 若已有 session 則傳入
 * @param {string|null} payload.name
 * @param {string|null} payload.summary
 * @param {string|null} payload.planType
 * @param {string[]|null} payload.channel
 * @param {string|null} payload.planJson
 * @param {Array} payload.newMessages        - [{role, content, msgType, metaJson}]
 * @returns {Promise<{buildAppId: string}>}
 */
export async function buildAppSaveChat(payload) {
  const res = await fetch(`${BASE_URL}/api/build-app/save-chat`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 取得 Apps 歷史列表
 * @param {number} page
 * @param {number} pageSize
 */
export async function buildAppList(page = 1, pageSize = 30) {
  const res = await fetch(`${BASE_URL}/api/build-app/list?page=${page}&pageSize=${pageSize}`, {
    headers: authHeaders()
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * 取得單一 BuildApp（含對話歷史）
 * @param {string} id  GUID
 */
export async function buildAppGet(id) {
  const res = await fetch(`${BASE_URL}/api/build-app/${id}`, {
    headers: authHeaders()
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * 重命名 BuildApp
 */
export async function buildAppRename(id, name) {
  const res = await fetch(`${BASE_URL}/api/build-app/${id}/rename`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ name })
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * 刪除 BuildApp
 */
export async function buildAppDelete(id) {
  const res = await fetch(`${BASE_URL}/api/build-app/${id}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
